/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    VodBrowseApi,
    VodItemsGetRequest,
    VodLaunchApi,
    VodLaunchDetails,
    VodLaunchInfoGetRequest,
    VodPurchaseApi,
    VodPurchasePostRequest,
    VodPurchaseStatus,
} from 'client-code/vod-browse';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IVodBrowseServiceRepository } from '../interfaces';
import { Log } from '../logging';
import { TServicesParams, TVodBrowseData } from '../types';

export class VodBrowseServiceRepositoryImpl implements IVodBrowseServiceRepository {
    private readonly TAG: string = this.constructor.name;

    private mCommonHeaders: CommonHeaders;

    private mServiceParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mCommonHeaders = new CommonHeaders('VODBrowse');
        this.mServiceParams = params;
    }

    private generateConfiguration(): Configuration {
        const oConfigParams: ConfigurationParameters = {
            basePath: this.mServiceParams.basePath,
            apiKey: this.mServiceParams.accessToken,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: this.mCommonHeaders.headers,
            },
        };
        return new Configuration(oConfigParams);
    }

    private getCommonRequestParams() {
        return {
            acceptLanguage: this.mCommonHeaders.headers['Accept-Language'],
            applicationName: this.mCommonHeaders.headers.ApplicationName,
            applicationFeatureArea: this.mCommonHeaders.headers.ApplicationFeatureArea,
            applicationVersion: this.mCommonHeaders.headers.ApplicationVersion,
            deviceType: this.mCommonHeaders.headers.DeviceType,
            productName: this.mCommonHeaders.headers.ProductName,
            bodyId: this.mServiceParams.bodyId,
            timeZoneName: this.mServiceParams.timeZoneName,
            whiny: this.mServiceParams.whiny || 'false',
        };
    }

    /**
     * @inheritdoc
     */
    public async fetchVodBrowseData(
        vodBrowseItemsParams: {
            parentVodItemId?: string;
            includeAdult?: boolean;
            offset?: number;
            limit?: number;
        } = {},
    ): Promise<TVodBrowseData> {
        const requestParameters: VodItemsGetRequest = {
            ...this.getCommonRequestParams(),
            // Specify a parent vodItemId to filter the response.
            // Only child vodItem of the given parent vodItem will be returned.
            parentVodItemId: vodBrowseItemsParams.parentVodItemId ?? '',
            // Default to false for includeAdult if not provided
            includeAdult: vodBrowseItemsParams.includeAdult ?? false,
            offset: vodBrowseItemsParams.offset ?? 0,
            limit: vodBrowseItemsParams.limit,
        };
        try {
            const configuration = this.generateConfiguration();
            const vodBrowseApi = new VodBrowseApi(configuration);
            const response = await vodBrowseApi.vodItemsGet(requestParameters);
            const totalCountHeader = response.headers['x-total-count'];
            const totalCount =
                totalCountHeader && !Number.isNaN(parseInt(totalCountHeader, 10))
                    ? parseInt(totalCountHeader, 10)
                    : 0;
            const vodItemList = response.data;
            return {
                vodItemList,
                totalCount,
            };
        } catch (error) {
            Log.e(this.TAG, 'fetchVodBrowseData() - failed to fetch Vod browse items', error, {
                requestParameters,
            });
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchVodLaunchInfo(vodJumpChannelNumber: string): Promise<VodLaunchDetails> {
        const requestParameters: VodLaunchInfoGetRequest = {
            ...this.getCommonRequestParams(),
            vodJumpChannelNumber,
        };

        try {
            const configuration = this.generateConfiguration();
            const vodLaunchApi = new VodLaunchApi(configuration);

            const response = await vodLaunchApi.vodLaunchInfoGet(requestParameters);
            return response.data;
        } catch (error) {
            Log.e(this.TAG, 'fetchVodLaunchInfo() - failed to fetch VOD launch info', error, {
                requestParameters,
            });
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async vodPurchasePost(vodPurchaseParams: {
        applicationFeatureArea: string;
        partnerOfferId: string;
        price?: string;
    }): Promise<VodPurchaseStatus> {
        try {
            const { applicationFeatureArea, partnerOfferId, price } = vodPurchaseParams;
            this.mCommonHeaders = new CommonHeaders(applicationFeatureArea);
            const requestParameters: VodPurchasePostRequest = {
                ...this.getCommonRequestParams(),
                vodPurchaseRequest: {
                    partnerOfferId,
                    price,
                },
            };

            const configuration = this.generateConfiguration();
            const vodPurchaseApi = new VodPurchaseApi(configuration);

            const response = await vodPurchaseApi.vodPurchasePost(requestParameters);
            return response.data;
        } catch (error) {
            Log.e(this.TAG, 'vodPurchasePost() - failed to purchase VOD item', error, {
                vodPurchaseParams,
            });
            throw error;
        }
    }
}
